
import React, { useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { IoClose } from "react-icons/io5";
import { API_BASE_URL } from "../../../../config";

const PERMISSIONS = {
  Projects: [
    { key: "project.add", label: "Add Project" },
    { key: "project.edit", label: "Edit Project" },
    { key: "project.delete", label: "Delete Project" },
    { key: "project.upload", label: "Upload Projects" },
  ],
  Customers: [
    { key: "customer.add", label: "Add Customer" },
    { key: "customer.edit", label: "Edit Customer" },
    { key: "customer.delete", label: "Delete Customer" },
    { key: "customer.upload", label: "Upload Customers" },
  ],
  Visits: [
    { key: "visit.add", label: "Add Visit" },
    { key: "visit.edit", label: "Edit Visit" },
  ],
  Bills: [
    { key: "bill.add", label: "Add Bill" },
    { key: "bill.edit", label: "Edit Bill" },
    { key: "bill.delete", label: "Delete Bill" },
  ],
  FinalBills: [
    { key: "finalBill.add", label: "Add Final Bill" },
  ],
};

const AddSubAdminForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    adminName: "",
    email: "",
    password: "",
  });

  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // Toggle permission
  const handlePermissionToggle = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  // Input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  
  const buildPermissionsPayload = () => {
    const permissionsObj = {
      project: { add: false, edit: false, delete: false, upload: false },
      customer: { add: false, edit: false, delete: false, upload: false },
      visit: { add: false, edit: false },
      bill: { add: false, edit: false, delete: false },
      finalBill: { add: false },
    };

    selectedPermissions.forEach((perm) => {
      const [module, action] = perm.split(".");
      if (permissionsObj[module] && permissionsObj[module][action] !== undefined) {
        permissionsObj[module][action] = true;
      }
    });

    return permissionsObj;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Adding sub-admin...");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/add-subadmin`,
        { ...formData, permissions: buildPermissionsPayload() },
        { headers: { token } }
      );

      toast.dismiss(loadingToast);

      if (response.data.success) {
        toast.success("Sub-admin added successfully!");
        setTimeout(onClose, 1500);
      } else {
        toast.error("Error: " + response.data.message);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      // console.error(error);
      toast.error("Failed to add sub-admin.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center overflow-y-auto">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="my-6 w-full max-w-2xl bg-white dark:bg-navy-800 rounded-lg shadow-xl p-4 sm:p-5 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            Add Sub Admin
          </h2>
          <button
            className="rounded bg-red-700 px-2 py-1 text-white transition hover:bg-red-800"
            onClick={onClose}
          >
            <IoClose size={22} />
          </button>
        </div>

        {/* Form */}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Admin Name
            </label>
            <input
              type="text"
              name="adminName"
              value={formData.adminName}
              onChange={handleChange}
              placeholder="Enter admin name"
              className="rounded-md border px-2 py-1.5 text-sm outline-none dark:bg-navy-700 dark:text-white"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              className="rounded-md border px-2 py-1.5 text-sm outline-none dark:bg-navy-700 dark:text-white"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-gray-700 dark:text-white">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password"
              className="rounded-md border px-2 py-1.5 text-sm outline-none dark:bg-navy-700 dark:text-white"
              required
            />
          </div>

          {/* Permissions */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              Assign Permissions
            </h3>
            {Object.entries(PERMISSIONS).map(([module, perms]) => (
              <div key={module} className="border rounded-lg p-3">
                <h4 className="font-medium mb-2 dark:text-white">{module}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {perms.map((perm) => (
                    <label key={perm.key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(perm.key)}
                        onChange={() => handlePermissionToggle(perm.key)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 "
                      />
                      <span className="text-sm dark:text-white">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-3 flex justify-end">
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-4 py-1.5 rounded-md text-sm transition"
            >
              Save Sub Admin
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSubAdminForm;



